import React, { useEffect, useRef, useState } from "react";
import { Pencil, Type, X, Check } from "lucide-react";

export default function SignaturePad({ onSign, onClose, defaultName = "" }) {
  const [tab, setTab] = useState("draw");
  const [typedName, setTypedName] = useState(defaultName);
  const [signerName, setSignerName] = useState(defaultName);
  const [hasDrawn, setHasDrawn] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);

  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    // Set actual canvas resolution
    const ratio = window.devicePixelRatio || 1;
    const rect = c.getBoundingClientRect();
    c.width = rect.width * ratio;
    c.height = rect.height * ratio;
    const ctx = c.getContext("2d");
    ctx.scale(ratio, ratio);
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = 2.5;
    ctx.strokeStyle = "#0F172A";
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
  }, [tab]);

  const getPos = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - rect.left, y: t.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const { x, y } = getPos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDrawn(true);
  };
  const stop = () => { isDrawing.current = false; };

  const clearCanvas = () => {
    const c = canvasRef.current;
    const ctx = c.getContext("2d");
    const rect = c.getBoundingClientRect();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, rect.width, rect.height);
    setHasDrawn(false);
  };

  const submit = () => {
    if (tab === "draw") {
      if (!hasDrawn) return alert("Por favor, dibuja tu firma");
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const base64 = dataUrl.split(",")[1];
      onSign({ type: "draw", data: base64, signer_name: signerName });
    } else {
      if (!typedName.trim()) return alert("Por favor, escribe tu nombre completo");
      onSign({ type: "type", data: typedName, signer_name: signerName || typedName });
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="signature-modal">
      <div className="bg-white max-w-lg w-full">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="font-display font-extrabold text-xl tracking-tight">Firmar presupuesto</h3>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-700" data-testid="signature-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="kbar block">Su nombre</label>
            <input
              data-testid="signature-signer-name"
              value={signerName}
              onChange={(e) => setSignerName(e.target.value)}
              placeholder="Nombre y apellidos"
              className="w-full h-11 mt-2 px-3 border border-slate-200 focus:border-orange-600 outline-none"
            />
          </div>

          <div className="flex gap-1 border-b border-slate-200">
            <TabBtn active={tab === "draw"} onClick={() => setTab("draw")} icon={Pencil} label="Dibujar" testid="tab-draw" />
            <TabBtn active={tab === "type"} onClick={() => setTab("type")} icon={Type} label="Escribir" testid="tab-type" />
          </div>

          {tab === "draw" && (
            <div>
              <div className="border-2 border-dashed border-slate-300 bg-white" style={{ touchAction: "none" }}>
                <canvas
                  ref={canvasRef}
                  data-testid="signature-canvas"
                  style={{ width: "100%", height: "180px", display: "block", cursor: "crosshair" }}
                  onMouseDown={start} onMouseMove={draw} onMouseUp={stop} onMouseLeave={stop}
                  onTouchStart={start} onTouchMove={draw} onTouchEnd={stop}
                />
              </div>
              <div className="flex justify-between mt-2">
                <button type="button" onClick={clearCanvas} data-testid="signature-clear" className="text-sm text-slate-500 hover:text-red-600">Borrar</button>
                <span className="text-xs text-slate-400">Firma con el dedo o el ratón</span>
              </div>
            </div>
          )}

          {tab === "type" && (
            <div>
              <input
                data-testid="signature-typed"
                value={typedName} onChange={(e) => setTypedName(e.target.value)}
                placeholder="Nombre completo"
                className="w-full h-12 px-3 border border-slate-200 focus:border-orange-600 outline-none"
              />
              <div className="mt-2 p-6 border-2 border-dashed border-slate-300 bg-white text-center">
                <span style={{ fontFamily: "cursive", fontSize: "32px", color: "#0F172A", fontStyle: "italic" }}>{typedName || "—"}</span>
              </div>
              <p className="text-xs text-slate-400 mt-2">Vista previa de tu firma tipográfica</p>
            </div>
          )}
        </div>

        <div className="p-5 border-t border-slate-200 flex gap-2 justify-end">
          <button onClick={onClose} className="h-11 px-4 border border-slate-200 hover:border-slate-300 text-sm font-semibold">Cancelar</button>
          <button onClick={submit} data-testid="signature-submit" className="btn-brand h-11 px-5 inline-flex items-center gap-2"><Check className="w-4 h-4" /> Firmar</button>
        </div>
      </div>
    </div>
  );
}

function TabBtn({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      type="button" onClick={onClick} data-testid={testid}
      className={`px-4 h-10 text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 transition-colors ${active ? "text-orange-600 border-b-2 border-orange-600 -mb-px" : "text-slate-500 hover:text-slate-700"}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </button>
  );
}
